/**
 * Users Management Page
 * Admin-sida för att hantera användare
 * Stödjer både vanliga användare, drivers och SSO-användare
 */

import { useState, useMemo, useEffect } from 'react';
import { userMatchesSearch } from '@/utils/userSearch';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  UserIcon,
  IdentificationIcon,
  TruckIcon,
  PhoneIcon,
  UsersIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { usersService, type User } from '@/services/users/usersService';
import { useUsersManagement } from '@/hooks/useUsersManagement';
import { usePermissions } from '@/hooks/usePermissions';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import { isSysAdminUser } from '@/utils/isSysAdminUser';
import UserFormFields from '@/components/users/UserFormFields';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import UserImportModal from '@/components/users/UserImportModal';
import { UserListScroller } from '@/components/users/UserListScroller';
import api from '@/services/api/client';
import toast from 'react-hot-toast';

// Endast lokala lösenordskonton kan vara "ej verifierade" / sakna lösenord.
// SSO-användare (SAML2/OIDC) loggar in via federerad inloggning.
const isLocalAuth = (u: User): boolean =>
  u.auth_method !== 'SAML2' && u.auth_method !== 'OIDC';

// Visa senaste inloggning i användarens lokala tid (UTC från API → lokal).
const formatLastLogin = (iso: string): string =>
  new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(new Date(iso));

const UsersManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { permissions, isUnlimited } = usePermissions();
  const isSysAdmin = isSysAdminUser(currentUser);
  const [ssoHardDeleteEnabled, setSsoHardDeleteEnabled] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const {
    users,
    organizations,
    loading,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    selectedUser,
    formData,
    setFormData,
    driverSetupDraft,
    setDriverSetupDraft,
    sendWelcomeEmail,
    setSendWelcomeEmail,
    resetForm,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleHardDeleteUser,
    enableDriverOnUser,
    openEditModal,
    openCreateModal,
    fetchUsers,
  } = useUsersManagement();

  const [orgFilter, setOrgFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [sendingBulkWelcome, setSendingBulkWelcome] = useState(false);
  const hasExplicitAction = (...actionCodes: string[]) => {
    if (isUnlimited) return true;
    return (
      permissions?.permissions?.some(
        (permission) =>
          permission.feature.code === 'ADMIN_USERS' &&
          (permission.action === null ||
            (permission.action?.code != null &&
              actionCodes.includes(permission.action.code)))
      ) ?? false
    );
  };
  const canCreateUsers = hasExplicitAction('CREATE');
  const canImportUsers = hasExplicitAction('IMPORT');
  const canUpdateUsers = hasExplicitAction('UPDATE', 'EDIT');
  const canDeleteUsers = hasExplicitAction('DELETE');
  const showSsoHardDelete = isSysAdmin && ssoHardDeleteEnabled;

  useEffect(() => {
    if (!isSysAdmin) return;
    let cancelled = false;
    usersService
      .getSsoRetestTools()
      .then((tools) => {
        if (!cancelled) setSsoHardDeleteEnabled(tools.hardDeleteEnabled);
      })
      .catch(() => {
        if (!cancelled) setSsoHardDeleteEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSysAdmin]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/users/import-template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'anvandare-importmall.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('usersManagement.downloadTemplateError'));
    }
  };

  const availableRoles = useMemo(() => {
    const roles = new Set(users.map(u => u.role).filter((r): r is string => !!r));
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchFilter.trim().toLowerCase();

    const filtered = users.filter(u => {
      if (orgFilter && u.organization_id !== orgFilter) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (normalizedSearch) {
        if (!userMatchesSearch(u, normalizedSearch)) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      const byFirst = (a.first_name || '').localeCompare(b.first_name || '', 'sv', { sensitivity: 'base' });
      if (byFirst !== 0) return byFirst;
      return (a.last_name || '').localeCompare(b.last_name || '', 'sv', { sensitivity: 'base' });
    });
  }, [users, orgFilter, roleFilter, searchFilter]);

  const hasActiveFilters = orgFilter || roleFilter || searchFilter.trim();

  const openUser = async (userItem: User) => {
    setOpeningUserId(userItem.id);
    try {
      await openEditModal(userItem);
    } finally {
      setOpeningUserId(null);
    }
  };

  // Mottagare = ALLA användare som just nu visas i den filtrerade listan.
  // Backend hoppar säkert över de som inte kan få en aktiveringslänk
  // (saknar e-post, SSO-inloggning eller inaktiv/låst) och rapporterar orsak.
  const welcomeEmailRecipients = filteredUsers;

  const handleSendWelcomeEmailBulk = () => {
    if (welcomeEmailRecipients.length === 0) {
      toast.error(t('usersManagement.sendWelcomeEmailBulkNone'));
      return;
    }

    confirm({
      title: t('usersManagement.sendWelcomeEmailBulk'),
      message: t('usersManagement.sendWelcomeEmailBulkConfirm', {
        count: welcomeEmailRecipients.length,
      }),
      onConfirm: async () => {
        setSendingBulkWelcome(true);
        try {
          const response = await api.post(
            '/api/users/send-welcome-bulk',
            { userIds: welcomeEmailRecipients.map((u) => u.id) },
            // Många mejl skickas i följd på backend – tillåt längre tid än standard (30s)
            { timeout: 5 * 60 * 1000 }
          );
          const { sentCount, skippedCount, failedCount, skipped } =
            response.data.data as {
              sentCount: number;
              skippedCount: number;
              failedCount: number;
              skipped?: Array<{ userId: string; reason: string }>;
            };
          if (sentCount > 0) {
            toast.success(
              t('usersManagement.sendWelcomeEmailBulkDone', { sent: sentCount })
            );
          }
          if (skippedCount > 0 || failedCount > 0) {
            const reasonSummary = summarizeSkipReasons(skipped);
            toast.error(
              reasonSummary
                ? `${t('usersManagement.sendWelcomeEmailBulkPartial', {
                    skipped: skippedCount,
                    failed: failedCount,
                  })} (${reasonSummary})`
                : t('usersManagement.sendWelcomeEmailBulkPartial', {
                    skipped: skippedCount,
                    failed: failedCount,
                  }),
              { duration: 8000 }
            );
          }
          if (sentCount === 0) {
            toast.error(t('usersManagement.sendWelcomeEmailBulkNone'));
          }
        } catch {
          toast.error(t('usersManagement.sendWelcomeEmailError'));
        } finally {
          setSendingBulkWelcome(false);
        }
      },
    });
  };

  // Bygger en läsbar sammanfattning per orsak, t.ex. "3 saknar e-post, 2 SSO-inloggning".
  function summarizeSkipReasons(
    skipped?: Array<{ userId: string; reason: string }>
  ): string {
    if (!skipped || skipped.length === 0) return '';
    const reasonKey: Record<string, string> = {
      NO_EMAIL: 'usersManagement.welcomeSkipNoEmail',
      SSO_USER: 'usersManagement.welcomeSkipSso',
      INACTIVE: 'usersManagement.welcomeSkipInactive',
      NOT_FOUND: 'usersManagement.welcomeSkipNotFound',
      EMAIL_DISABLED: 'usersManagement.welcomeSkipEmailOff',
    };
    const counts = new Map<string, number>();
    for (const s of skipped) {
      counts.set(s.reason, (counts.get(s.reason) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([reason, count]) => {
        const key = reasonKey[reason];
        const label = key ? t(key) : reason;
        return `${count} ${label}`;
      })
      .join(', ');
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-textTertiary">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-bgContainer p-4">
      <div className="mb-6 flex shrink-0 justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <UsersIcon className="w-6 h-6 text-primary" />
            {t('usersManagement.title')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canUpdateUsers && (
            <button
              onClick={handleSendWelcomeEmailBulk}
              disabled={sendingBulkWelcome || welcomeEmailRecipients.length === 0}
              className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgElevated transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
              title={t('usersManagement.sendWelcomeEmailBulkHint')}
            >
              <EnvelopeIcon className="w-4 h-4" />
              {sendingBulkWelcome
                ? t('usersManagement.sendWelcomeEmailSending')
                : t('usersManagement.sendWelcomeEmailBulk')}
            </button>
          )}
          {canImportUsers && (
            <>
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgElevated transition-colors flex items-center gap-2 text-sm"
                title={t('usersManagement.downloadTemplate')}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {t('usersManagement.downloadTemplate')}
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgElevated transition-colors flex items-center gap-2 text-sm"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                {t('usersManagement.importUsers')}
              </button>
            </>
          )}
          {canCreateUsers && (
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 text-sm"
            >
              <PlusIcon className="w-5 h-5" />
              {t('usersManagement.createUser')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
        <FunnelIcon className="w-4 h-4 text-textSecondary flex-shrink-0" />

        <div className="min-w-[250px]">
          <HierarchicalOrgSelect
            value={orgFilter}
            onChange={setOrgFilter}
            emptyLabel={t('common.allOrganizations')}
            showEmptyOption={true}
          />
        </div>

        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="border border-border rounded-lg px-3 py-1.5 text-sm bg-bgDefault text-textDefault min-w-[150px]"
        >
          <option value="">{t('common.allRoles')}</option>
          {availableRoles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>

        <div className="relative min-w-[260px]">
          <MagnifyingGlassIcon className="w-4 h-4 text-textSecondary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder={t('usersManagement.searchPlaceholder')}
            className="w-full border border-border rounded-lg pl-9 pr-3 py-1.5 text-sm bg-bgDefault text-textDefault"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={() => { setOrgFilter(''); setRoleFilter(''); setSearchFilter(''); }}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            {t('common.clearFilters')}
          </button>
        )}

        <span className="text-xs text-textSecondary ml-auto">
          {filteredUsers.length} / {users.length} {t('usersManagement.usersCount')}
        </span>
      </div>

      {/* Users list */}
      <UserListScroller
        items={filteredUsers}
        rowHeight={240}
        getKey={(userItem) => userItem.id}
        renderItem={(userItem) => (
          <div className="h-full overflow-hidden rounded-lg border border-border bg-bgElevated p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <UserIcon className="w-6 h-6 text-textSecondary" />
                  <button
                    type="button"
                    disabled={!canUpdateUsers || openingUserId === userItem.id}
                    onClick={() => void openUser(userItem)}
                    className="text-left disabled:cursor-default"
                  >
                    <h3 className="text-xl font-semibold text-textDefault">
                      {userItem.first_name && userItem.last_name
                        ? `${userItem.first_name} ${userItem.last_name}`
                        : userItem.email}
                    </h3>
                    <p className="text-sm text-textSecondary mt-1">{userItem.email}</p>
                  </button>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    userItem.status === 'ACTIVE' 
                      ? 'bg-green-100 text-green-800' 
                      : userItem.status === 'INACTIVE'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {userItem.status}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-bgLight text-textDefault">
                    {userItem.role_name || userItem.role}
                  </span>
                  {(userItem.auth_method === 'SAML2' || userItem.auth_method === 'OIDC') && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                      SSO
                    </span>
                  )}
                  {userItem.driver_id && (
                    <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-800 flex items-center gap-1">
                      <IdentificationIcon className="w-3 h-3" />
                      Driver
                    </span>
                  )}
                  {userItem.current_vehicle_registration && (
                    <span className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-800 flex items-center gap-1">
                      <TruckIcon className="w-3 h-3" />
                      {t('usersManagement.connectedVehicle', { reg: userItem.current_vehicle_registration })}
                    </span>
                  )}
                  {userItem.is_global_admin && (
                    <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                      Global Admin
                    </span>
                  )}
                  {isLocalAuth(userItem) && userItem.email_verified === false && (
                    <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800 flex items-center gap-1">
                      <ExclamationTriangleIcon className="w-3 h-3" />
                      {t('usersManagement.notVerified')}
                    </span>
                  )}
                  {isLocalAuth(userItem) && !userItem.last_login && (
                    <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">
                      {t('usersManagement.neverLoggedIn')}
                    </span>
                  )}
                  {isLocalAuth(userItem) && userItem.has_password === false && (
                    <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">
                      {t('usersManagement.noPassword')}
                    </span>
                  )}
                </div>

                <div className="mt-2 text-xs text-textSecondary">
                  {userItem.last_login
                    ? t('usersManagement.lastLoginAt', { date: formatLastLogin(userItem.last_login) })
                    : t('usersManagement.neverLoggedIn')}
                </div>

                {userItem.MobilePhone && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-textSecondary">
                    <PhoneIcon className="w-4 h-4" />
                    {userItem.MobilePhone}
                  </div>
                )}
              </div>

              <div className="flex gap-2 ml-4">
                {canUpdateUsers && (
                  <button
                    onClick={() => void openUser(userItem)}
                    disabled={openingUserId === userItem.id}
                    className="p-2 text-textSecondary hover:text-textDefault hover:bg-bgLight rounded transition-colors"
                    title={t('usersManagement.editUserTitle')}
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                )}
                {canDeleteUsers && (
                  <button
                    onClick={() =>
                      confirm({
                        title: t('common.confirm') ,
                        message: t('admin.deleteUserConfirm') ,
                        onConfirm: () => handleDeleteUser(userItem.id),
                        variant: 'danger',
                      })
                    }
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title={t('usersManagement.deleteUserTitle')}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
                {showSsoHardDelete && (
                  <button
                    type="button"
                    onClick={() =>
                      confirm({
                        title: t('usersManagement.hardDeleteTitle'),
                        message: t('usersManagement.hardDeleteConfirm', {
                          name: `${userItem.first_name || ''} ${userItem.last_name || ''}`.trim() || userItem.email,
                          email: userItem.email,
                        }),
                        onConfirm: () => handleHardDeleteUser(userItem.id),
                        variant: 'danger',
                      })
                    }
                    className="px-2 py-1 text-xs font-semibold text-red-700 border border-red-300 hover:bg-red-50 rounded transition-colors"
                    title={t('usersManagement.hardDeleteTitle')}
                  >
                    {t('usersManagement.hardDeleteButton')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bgContainer rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-textDefault mb-4">{t('usersManagement.createUser')}</h2>
            
            <UserFormFields
              formData={formData}
              setFormData={setFormData}
              organizations={organizations}
              isEdit={false}
              driverSetupDraft={driverSetupDraft}
              onDriverSetupDraftChange={setDriverSetupDraft}
            />

            {formData.auth_method !== 'SAML2' && formData.auth_method !== 'OIDC' && (
              <label className="mt-4 flex items-start gap-3 border border-border rounded-lg p-3 bg-bgLight cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWelcomeEmail}
                  onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-bgPrimary focus:ring-bgPrimary"
                />
                <span>
                  <span className="text-sm font-medium text-textDefault">
                    {t('usersManagement.sendWelcomeEmail')}
                  </span>
                  <p className="text-xs text-textSecondary mt-0.5">
                    {t('usersManagement.sendWelcomeEmailDesc')}
                  </p>
                </span>
              </label>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgLight transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('usersManagement.createUser')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bgContainer rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-textDefault mb-4">{t('usersManagement.editUser')}</h2>
            
            <UserFormFields
              formData={formData}
              setFormData={setFormData}
              organizations={organizations}
              isEdit={true}
              driverDbId={selectedUser?.driver?.id || selectedUser?.driver_table_id ? Number(selectedUser?.driver?.id || selectedUser?.driver_table_id) : undefined}
              userId={selectedUser?.id}
              onEnableDriver={enableDriverOnUser}
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgLight transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpdateUser}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog />

      {/* Import Modal */}
      {showImportModal && (
        <UserImportModal
          organizations={organizations}
          onClose={() => setShowImportModal(false)}
          onImportDone={() => {
            fetchUsers?.();
          }}
        />
      )}
    </div>
  );
};

export default UsersManagement;
